import React, { useEffect, useState } from 'react';

import { Autocomplete, Box, Chip, FormControlLabel, Slider, Switch, TextField, Typography } from '@mui/material';

import { X } from 'lucide-react';

import { useAppDispatch, useAppSelector } from '../../hooks';
import { featureFlagsReducer } from '../../store/reducers/featureFlags.reducer';
import { getIsCreatingFeatureFlag } from '../../store/selectors/featureFlags.selector';
import { getAllUsers } from '../../store/selectors/user.selector';
import { greyP5 } from '../../theme.ts';
import { CreateFeatureFlagRequest } from '../../types/featureFlags';
import { User } from '../../types/user';

const { createFeatureFlag } = featureFlagsReducer.actions;

interface AddFeatureFlagModalProps {
  open: boolean;
  onClose: () => void;
}

export const AddFeatureFlagModal = ({ open, onClose }: AddFeatureFlagModalProps) => {
  const dispatch = useAppDispatch();
  const isCreating = useAppSelector(getIsCreatingFeatureFlag);
  const allUsers = useAppSelector(getAllUsers);

  const [formData, setFormData] = useState<{
    key: string;
    name: string;
    description: string;
    enabled: boolean;
    rolloutPercentage: number;
    selectedUsers: User[];
  }>({
    key: '',
    name: '',
    description: '',
    enabled: false,
    rolloutPercentage: 0,
    selectedUsers: [],
  });

  const [errors, setErrors] = useState<{
    key: string;
    name: string;
    description: string;
    rolloutPercentage: string;
  }>({
    key: '',
    name: '',
    description: '',
    rolloutPercentage: '',
  });

  // Reset form data when modal opens
  useEffect(() => {
    if (open) {
      setFormData({
        key: '',
        name: '',
        description: '',
        enabled: false,
        rolloutPercentage: 0,
        selectedUsers: [],
      });

      // Clear errors when opening
      setErrors({
        key: '',
        name: '',
        description: '',
        rolloutPercentage: '',
      });
    }
  }, [open]);

  const validateForm = (): boolean => {
    const newErrors = {
      key: '',
      name: '',
      description: '',
      rolloutPercentage: '',
    };

    if (!formData.key.trim()) {
      newErrors.key = 'Key is required';
    }

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (formData.rolloutPercentage < 0 || formData.rolloutPercentage > 100) {
      newErrors.rolloutPercentage = 'Rollout percentage must be between 0 and 100';
    }

    setErrors(newErrors);
    return !Object.values(newErrors).some((error) => error !== '');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const createRequest: CreateFeatureFlagRequest = {
      key: formData.key.trim(),
      name: formData.name.trim(),
      description: formData.description.trim(),
      enabled: formData.enabled,
      rolloutPercentage: formData.rolloutPercentage,
      userTargets: formData.selectedUsers.map((user) => user.id),
    };

    dispatch(createFeatureFlag(createRequest));
    onClose();
  };

  const handleClose = () => {
    if (!isCreating) {
      onClose();
    }
  };

  // Handle Esc key to close the modal
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape' && !isCreating) {
      event.stopPropagation();
      onClose();
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4"
      onKeyDown={handleKeyDown}
    >
      <div className="rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto" style={{ backgroundColor: greyP5 }}>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-white">Add Feature Flag</h2>
          <button onClick={handleClose} className="text-gray-400 hover:text-white" disabled={isCreating}>
            <X size={20} />
          </button>
        </div>

        <div className="mb-6">
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              label="Key"
              value={formData.key}
              onChange={(e) => setFormData({ ...formData, key: e.target.value })}
              error={!!errors.key}
              helperText={errors.key || 'Unique identifier for the feature flag'}
              disabled={isCreating}
            />

            <TextField
              margin="normal"
              required
              fullWidth
              label="Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              error={!!errors.name}
              helperText={errors.name}
              disabled={isCreating}
            />

            <TextField
              margin="normal"
              fullWidth
              multiline
              rows={3}
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              error={!!errors.description}
              helperText={errors.description}
              disabled={isCreating}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.enabled}
                  onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                  disabled={isCreating}
                />
              }
              label="Enabled"
              sx={{ mt: 2, mb: 2 }}
            />

            <Box sx={{ mt: 3, mb: 3 }}>
              <Typography gutterBottom>Rollout Percentage: {formData.rolloutPercentage}%</Typography>
              <Slider
                value={formData.rolloutPercentage}
                onChange={(_, newValue) => setFormData({ ...formData, rolloutPercentage: newValue as number })}
                step={1}
                marks
                min={0}
                max={100}
                valueLabelDisplay="auto"
                disabled={isCreating}
              />
              {errors.rolloutPercentage && (
                <Typography color="error" variant="caption">
                  {errors.rolloutPercentage}
                </Typography>
              )}
            </Box>

            <Autocomplete
              multiple
              options={allUsers}
              getOptionLabel={(option) => `${option.fullName} (${option.email})`}
              value={formData.selectedUsers}
              onChange={(_, newValue) => setFormData({ ...formData, selectedUsers: newValue })}
              disabled={isCreating}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Target Users"
                  helperText="Select users to target with this feature flag"
                />
              )}
              renderTags={(tagValue, getTagProps) =>
                tagValue.map((option, index) => (
                  <Chip label={`${option.fullName} (${option.email})`} {...getTagProps({ index })} key={option.id} />
                ))
              }
              sx={{ mt: 2 }}
            />
          </Box>
        </div>

        <div className="flex justify-end space-x-3 pt-4">
          <button
            type="button"
            onClick={handleClose}
            disabled={isCreating}
            className="px-4 py-2 rounded bg-gray-700 hover:bg-gray-600"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isCreating}
            className="px-4 py-2 rounded text-black"
            style={{
              backgroundColor: '#B3EC49',
            }}
            onMouseEnter={(e) => {
              if (!isCreating) {
                (e.target as HTMLButtonElement).style.backgroundColor = 'rgba(179, 236, 73, 0.8)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isCreating) {
                (e.target as HTMLButtonElement).style.backgroundColor = '#B3EC49';
              }
            }}
          >
            {isCreating ? 'Creating...' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
};
